import { useLanguage } from '../LanguageContext';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Paper, TextField, Typography } from '@mui/material';
import axiosInstance from '../axiosInstance';
import { useBranding } from '../BrandingContext';

export default function BrandingSettings() {
  const { t } = useLanguage();
  const { branding, setBranding, loading, error: loadError } = useBranding();
  const [draft, setDraft] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(0);
  const [message, setMessage] = useState(null);
  useEffect(() => { setDraft(branding); }, [branding]);
  const change = key => event => { setDraft({ ...draft, [key]: event.target.value }); setMessage(null); };
  const upload = key => async event => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 512 * 1024 || !['image/png', 'image/jpeg', 'image/webp', 'image/x-icon', 'image/vnd.microsoft.icon'].includes(file.type)) {
      setMessage({ severity: 'error', text: t("Choose a PNG, JPEG, WebP or ICO image of at most 512 KB.") }); return;
    }
    setReading(value => value + 1);
    try {
      const image = await new Promise((resolve, reject) => {
        const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file);
      });
      setDraft(current => ({ ...current, [key]: image })); setMessage(null);
    } catch { setMessage({ severity: 'error', text: t("The image could not be read.") }); }
    finally { setReading(value => value - 1); }
  };
  const save = async event => {
    event.preventDefault();
    if (saving || reading) return;
    setSaving(true); setMessage(null);
    try {
      const { data } = await axiosInstance.put('/api/branding', draft);
      if (!data.success) throw new Error(data.error || t("Branding could not be saved."));
      setBranding(data.branding);
      setMessage({ severity: 'success', text: t("Branding saved. The navigation, browser title and favicon are updated.") });
    } catch (error) { setMessage({ severity: 'error', text: error.response?.data?.error || error.message }); }
    finally { setSaving(false); }
  };
  return <Container maxWidth="md">
    <Typography variant="h4" component="h1" gutterBottom>{t("Site appearance")}</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>{t("Superadmin settings · Customize the identity of your admin panel.")}</Typography>
    {loadError && <Alert severity="error" sx={{ mb: 2 }}>{t(loadError)}</Alert>}
    {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{t(message.text)}</Alert>}
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} component="form" onSubmit={save}>
      <fieldset disabled={saving || loading || Boolean(loadError)} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          <TextField label={t("Site name")} required inputProps={{ maxLength: 80 }} value={draft.name} onChange={change('name')} />
          <TextField label={t("Description")} multiline minRows={2} inputProps={{ maxLength: 500 }} helperText={t("Shown below the name and used as the page description.")} value={draft.description} onChange={change('description')} />
          <TextField label={t("Browser tab title")} required inputProps={{ maxLength: 120 }} value={draft.site_title} onChange={change('site_title')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {['logo', 'favicon'].map(key => <Box key={key}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{key === 'logo' ? t("Logo") : t("Favicon")}</Typography>
              <Box sx={{ height: 88, mb: 1, p: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {draft[key] ? <img src={draft[key]} alt={t("{asset} preview", { asset: t(key) })} style={{ maxWidth: '100%', maxHeight: 64, objectFit: 'contain' }} /> : <Typography color="text.secondary">{t("Default {asset}", { asset: t(key) })}</Typography>}
              </Box>
              <Button component="label" variant="outlined" disabled={reading > 0 || saving}>
                {t("Upload {asset}", { asset: t(key) })}
                <input hidden aria-label={t("Upload {asset}", { asset: t(key) })} type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon" onChange={upload(key)} disabled={reading > 0 || saving} />
              </Button>
              {draft[key] && <Button onClick={() => setDraft(current => ({ ...current, [key]: '' }))}>{t("Use default {asset}", { asset: t(key) })}</Button>}
            </Box>)}
          </Box>
          <Typography variant="caption" color="text.secondary">{t("PNG, JPEG, WebP or ICO · Up to 512 KB each. A square image works best for the favicon.")}</Typography>
          <Box className="brand" sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {draft.logo ? <img src={draft.logo} alt={t("Logo preview")} className="brand-logo" /> : <span className="brand-mark" aria-hidden="true">{'//'}</span>}
            <div>{draft.name}<small>{draft.description}</small></div>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={saving || loading || reading > 0 || Boolean(loadError) || !draft.name.trim() || !draft.site_title.trim()}>{saving ? t("Saving…") : t("Save appearance")}</Button>
            <Button disabled={saving || reading > 0} onClick={() => { setDraft(branding); setMessage(null); }}>{t("Discard changes")}</Button>
          </Box>
        </Box>
      </fieldset>
    </Paper>
  </Container>;
}
